import React, { useRef, useEffect, useState, useCallback } from 'react'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'

/**
 * Signature capture, shop-app style: the inline block only shows the current
 * signature (or a "Tap to sign" prompt). The actual signing happens in a
 * FULL-SCREEN overlay that renders SIDEWAYS (landscape) on portrait phones,
 * so there is real room to sign with a finger.
 */

// Map a screen point to canvas-local coordinates. When the overlay is CSS-
// rotated 90° clockwise, the canvas's local x axis points down the screen and
// its local y axis points left — derived from the rotated bounding box.
function getCanvasPoint(canvas, clientX, clientY, isRotated) {
  const rect = canvas.getBoundingClientRect()
  if (isRotated) {
    return { x: clientY - rect.top, y: rect.right - clientX }
  }
  return { x: clientX - rect.left, y: clientY - rect.top }
}

const SignatureOverlay = ({ prompt, onDone, onCancel }) => {
  const canvasRef = useRef(null)
  const drawingRef = useRef(false)
  const lastPosRef = useRef({ x: 0, y: 0 })
  const isRotatedRef = useRef(false)
  const [hasInk, setHasInk] = useState(false)
  const [viewport, setViewport] = useState(() => ({
    w: window.innerWidth,
    h: window.innerHeight,
  }))

  const isRotated = viewport.h > viewport.w
  isRotatedRef.current = isRotated

  // The page behind must not scroll while signing.
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight })
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Size the drawing buffer to the canvas's layout box (unaffected by the
  // CSS rotation) at device-pixel resolution.
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ratio = window.devicePixelRatio || 1
    canvas.width = Math.max(1, canvas.offsetWidth * ratio)
    canvas.height = Math.max(1, canvas.offsetHeight * ratio)
    const ctx = canvas.getContext('2d')
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    ctx.fillStyle = '#FFFFFF'
    ctx.fillRect(0, 0, canvas.offsetWidth, canvas.offsetHeight)
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#111111'
    setHasInk(false)
  }, [])

  useEffect(() => {
    setupCanvas()
  }, [setupCanvas, viewport.w, viewport.h])

  const handlePointerDown = (e) => {
    e.preventDefault()
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.setPointerCapture?.(e.pointerId)
    drawingRef.current = true
    lastPosRef.current = getCanvasPoint(canvas, e.clientX, e.clientY, isRotatedRef.current)
  }

  const handlePointerMove = (e) => {
    if (!drawingRef.current) return
    e.preventDefault()
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const point = getCanvasPoint(canvas, e.clientX, e.clientY, isRotatedRef.current)
    ctx.beginPath()
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y)
    ctx.lineTo(point.x, point.y)
    ctx.stroke()
    lastPosRef.current = point
    if (!hasInk) setHasInk(true)
  }

  const handlePointerUp = () => {
    drawingRef.current = false
  }

  const handleDone = () => {
    const canvas = canvasRef.current
    if (!canvas || !hasInk) return
    onDone(canvas.toDataURL('image/png'))
  }

  // Portrait phone: rotate the whole signing surface 90° so it uses the long
  // side of the screen (the shop-app behavior). Landscape/desktop: no rotation.
  const surfaceStyle = isRotated
    ? {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: `${viewport.h}px`,
        height: `${viewport.w}px`,
        transform: 'translate(-50%, -50%) rotate(90deg)',
      }
    : { position: 'absolute', inset: 0 }

  return (
    <div className="fixed inset-0 z-[95] bg-background">
      <div style={surfaceStyle} className="flex flex-col p-4 gap-3">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-medium text-foreground flex-1 min-w-0">{prompt}</p>
          <Button
            variant="ghost"
            size="icon"
            onClick={onCancel}
            aria-label="Cancel signing"
            className="flex-shrink-0 text-muted-foreground"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <canvas
          ref={canvasRef}
          className="flex-1 w-full min-h-0 rounded-xl border-2 border-dashed border-border/80 touch-none cursor-crosshair"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={handlePointerUp}
        />

        <div className="grid grid-cols-2 gap-3">
          <Button variant="outline" size="lg" onClick={setupCanvas} iconName="Eraser" iconPosition="left">
            Clear
          </Button>
          <Button variant="default" size="lg" onClick={handleDone} disabled={!hasInk} iconName="Check" iconPosition="left">
            Done
          </Button>
        </div>
      </div>
    </div>
  )
}

const SignaturePad = ({
  onSignature,
  label = 'Signature',
  prompt = 'Please sign in the box below',
  terms = '',
  initialSignature = null,
}) => {
  const [signature, setSignature] = useState(initialSignature)
  const [isSigning, setIsSigning] = useState(false)

  const handleDone = (dataUrl) => {
    setSignature(dataUrl)
    if (onSignature) onSignature(dataUrl)
    setIsSigning(false)
  }

  const clearSignature = () => {
    setSignature(null)
    if (onSignature) onSignature(null)
  }

  return (
    <div className="space-y-3">
      <div className="text-center">
        <h3 className="font-semibold text-foreground">{label}</h3>
        <p className="text-sm text-muted-foreground mt-1">{prompt}</p>
      </div>

      {signature ? (
        <>
          <img
            src={signature}
            alt="Signature"
            className="w-full h-40 object-contain border-2 border-border/60 rounded-xl bg-white"
          />
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={() => setIsSigning(true)} iconName="Pen" iconPosition="left">
              Sign Again
            </Button>
            <Button variant="ghost" onClick={clearSignature} iconName="Eraser" iconPosition="left">
              Clear
            </Button>
          </div>
        </>
      ) : (
        <button
          type="button"
          onClick={() => setIsSigning(true)}
          className="w-full h-40 border-2 border-dashed border-border/60 rounded-xl bg-background/80 flex flex-col items-center justify-center gap-2 text-muted-foreground active:bg-muted/40 touch-manipulation"
        >
          <Icon name="Pen" size={32} className="text-primary" />
          <span className="font-medium text-foreground">Tap to sign</span>
          <span className="text-xs">Opens full screen, sideways</span>
        </button>
      )}

      {terms && <p className="text-xs text-muted-foreground text-center">{terms}</p>}

      {isSigning && (
        <SignatureOverlay
          prompt={prompt || label}
          onDone={handleDone}
          onCancel={() => setIsSigning(false)}
        />
      )}
    </div>
  )
}

export default SignaturePad
